import type { SystemRecOutput } from '@core/types';
import type { PortalViewModel } from './buildPortalViewModel';

import ashpImage from '../../../../../Data/ASHP.PNG?url';
import combiImage from '../../../../../Data/Combination.png?url';
import gshpImage from '../../../../../Data/GSHP.PNG?url';
import systemBoilerImage from '../../../../../Data/System-boiler.png?url';
import openVentedImage from '../../../../../Data/Open-vented-schematic.jpeg?url';
import gravityImage from '../../../../../Data/gravity.JPG?url';

interface PortalWowSummaryProps {
  output: SystemRecOutput;
  viewModel: PortalViewModel;
}

const disruptionCopy: Record<string, string> = {
  combi: 'Lower installation impact. No hot water cylinder needed.',
  storage_combi: 'Medium installation impact. Cylinder footprint reduced.',
  system: 'Medium installation impact. Cylinder retained for strong hot water delivery.',
  regular: 'Medium installation impact. Existing distribution often retained.',
  ashp: 'Higher installation impact. Outdoor unit and system optimisation required.',
  gshp: 'Higher installation impact. Ground array works required.',
};

function getHeroImage(primary: SystemRecOutput['primary']): { src: string; alt: string } {
  const boiler = (primary.boiler || '').toLowerCase();
  const water = (primary.water || '').toLowerCase();

  if (boiler.includes('gshp')) return { src: gshpImage, alt: 'Ground source heat pump schematic' };
  if (boiler.includes('ashp') || boiler.includes('heat_pump')) return { src: ashpImage, alt: 'Air source heat pump schematic' };
  if (boiler.includes('combi') || water.includes('on_demand') || water.includes('instant')) {
    return { src: combiImage, alt: 'Combination boiler layout' };
  }
  if (boiler.includes('system')) return { src: systemBoilerImage, alt: 'System boiler layout' };
  if (boiler.includes('regular') || water.includes('open_vented') || water.includes('vented')) {
    return { src: openVentedImage, alt: 'Open-vented system schematic' };
  }

  return { src: gravityImage, alt: 'Legacy gravity-fed heating layout' };
}

function annualCostRange(annualised: number | undefined): string {
  if (!annualised) return 'Estimated during technical survey';
  const min = Math.round(annualised * 0.9);
  const max = Math.round(annualised * 1.1);
  return `£${min.toLocaleString()}–£${max.toLocaleString()} per year`;
}

function highlightSection(sectionId: string): void {
  const node = document.getElementById(sectionId);
  if (!node) return;

  node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  node.classList.add('portal-highlight-target');
  window.setTimeout(() => node.classList.remove('portal-highlight-target'), 1600);
}

export function PortalWowSummary({ output, viewModel }: PortalWowSummaryProps) {
  const primary = output.primary;
  const hero = getHeroImage(primary);
  const heatDemand = output.inputEcho?.heatDemandKw;
  const flowRate = output.inputEcho?.flowRate;
  const assumptions = output.assumptions?.slice(0, 2) ?? [];

  const reliabilityEssential = primary.cons.length > 0 ? '✔' : '✔✔';
  const reliabilityPerformance = '✔✔';

  return (
    <section className="portal-wow" aria-label="Recommendation summary">
      <div className="portal-wow__hero">
        <img className="portal-wow__image" src={hero.src} alt={hero.alt} />
        <div className="portal-wow__hero-content">
          <p className="portal-wow__eyebrow">Recommended for Your Home</p>
          <h2 className="portal-wow__title">{primary.title}</h2>
          <p className="portal-wow__subtitle">{primary.summary || primary.why}</p>
          <p className="portal-wow__confidence">Confidence Level: {viewModel.header.confidenceLevel}</p>
        </div>
      </div>

      <div className="portal-wow__cards" id="what-this-means">
        <article className="portal-wow__card">
          <h3>Comfort</h3>
          <p>{primary.pros[0] || 'Consistent hot water and stable room comfort across the home.'}</p>
        </article>
        <article className="portal-wow__card">
          <h3>Running Costs</h3>
          <p>{annualCostRange(primary.lcoo?.annualizedCostGBP)}</p>
        </article>
        <article className="portal-wow__card">
          <h3>Disruption</h3>
          <p>{disruptionCopy[primary.boiler] || 'Medium installation impact with staged commissioning.'}</p>
        </article>
      </div>

      <div className="portal-wow__comparison">
        <h3>Essential vs Performance</h3>
        <table>
          <thead>
            <tr><th></th><th>Essential</th><th>Performance</th></tr>
          </thead>
          <tbody>
            <tr><td>Reliability</td><td>{reliabilityEssential}</td><td>{reliabilityPerformance}</td></tr>
            <tr><td>Controls</td><td>Basic</td><td>Load-comp</td></tr>
            <tr><td>Longevity</td><td>Good</td><td>Excellent</td></tr>
            <tr><td>Magnetic filter</td><td>Optional</td><td>Included</td></tr>
          </tbody>
        </table>
      </div>

      <div className="portal-wow__authority">
        <h3>Why this was recommended</h3>
        <ul>
          <li>Based on estimated heat demand of {typeof heatDemand === 'number' ? `${heatDemand.toFixed(1)} kW` : 'the available home data'}.</li>
          <li>Cold mains flow assessed at {typeof flowRate === 'number' ? `${flowRate} L/min` : 'survey estimate'}.</li>
          <li>Radiator-based heating circuit retained where practical.</li>
          <li>Future heat pump compatibility retained.</li>
        </ul>
      </div>

      <div className="portal-wow__confidence-box">
        <h3>Confidence Level: {viewModel.header.confidenceLevel}</h3>
        <p>Some inputs were estimated:</p>
        <ul>
          {assumptions.length > 0 ? assumptions.map((item) => <li key={item}>{item}</li>) : (
            <>
              <li>Heat loss inferred from property age and type.</li>
              <li>Radiator sizing not measured room-by-room.</li>
            </>
          )}
        </ul>
      </div>

      <div className="portal-wow__pills" aria-label="Recommendation explainers">
        <button onClick={() => highlightSection('decision-axis-anchor')}>Why load compensation?</button>
        <button onClick={() => highlightSection('swot-anchor')}>Why not a combi?</button>
        <button onClick={() => highlightSection('evidence-anchor')}>Why cylinder over combi?</button>
      </div>
    </section>
  );
}
